import React, { useState } from 'react';
import { Link } from 'gatsby';
import { useStaticQuery, graphql } from 'gatsby';
import Img from 'gatsby-image';
import BackgroundImg from 'gatsby-background-image';
import { Parallax, Background } from 'react-parallax';
import { LocalizedLink } from '../components/language/localized-link';
import { WithIntl } from '../components/language/with-intl.js';
import Layout from '../components/layout';
import SEO from '../components/seo';
import { useTranslation } from 'react-i18next';

const Contact = ({ language }) => {
    const data = useStaticQuery(graphql`
        query {
            image: file(relativePath: { eq: "about/about6.jpg" }) {
                childImageSharp {
                    fixed(quality: 90, width: 2200) {
                        ...GatsbyImageSharpFixed
                    }
                    fluid(quality: 90) {
                        ...GatsbyImageSharpFluid
                    }
                }
            }
        }
    `);
    const { t } = useTranslation();

    return (
        <Layout>
            <SEO title={t('contact.seo_title')} description={t('contact.seo_description')} />
            <Parallax strength={600} className="d-none d-lg-block">
                <Background>
                    <Img fixed={data.image.childImageSharp.fixed} />
                </Background>
                <div className="py-10 py-md-15 overlay overlay-50 overlay-secondary">
                    <div className="container py-md-10">
                        <div className="row justify-content-center">
                            <div className="col-12 col-md-10 text-center">
                                <h1 className="display-2 font-weight-bold text-white">{t('contact.header')}</h1>
                                <p className="lead mb-8 text-white font-weight-bold">{t('contact.subheader')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </Parallax>
            <div className="d-lg-none img-cover mb-n12 bg-secondary">
                <div className="w-100">
                    <Img fluid={data.image.childImageSharp.fluid} style={{ opacity: 0.5 }} />
                    <div className="container mt-n15 mb-13 mt-lg-0">
                        <div className="row justify-content-center">
                            <div className="col-12 col-md-10 text-center">
                                <h1 className="display-2 font-weight-bold text-white mt-12 mt-md-0">{t('contact.header')}</h1>
                                <p className="lead mb-8 mt-11 mt-md-0 text-white font-weight-bold">{t('contact.subheader')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="position-relative d-none d-lg-block">
                <div className="shape shape-bottom shape-fluid-x svg-shim text-white">
                    <svg viewBox="0 0 2880 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z" fill="currentColor" />
                    </svg>
                </div>
            </div>
            <section className="mt-xl-10 py-7 py-md-9" id="info">
                <div className="container">
                    <div className="row">
                        <a
                            href="https://m.me/wesolvegr"
                            target="_blank"
                            rel="noreferrer noopener"
                            className="col-12 col-md-4 text-center border-right border-gray-300 hover-shadow text-decoration-none"
                        >
                            <h6 className="text-uppercase text-gray-700 mb-1 pt-8">{t('contact.messenger_heading')}</h6>
                            <div className="mb-5 mb-md-0 pb-8">
                                <h4 className="text-primary">{t('contact.messenger')} Messenger</h4>
                            </div>
                        </a>
                        <a href="tel:+30 698 375 3499" className="col-12 col-md-4 text-center border-right border-gray-300 hover-shadow text-decoration-none">
                            <h6 className="text-uppercase text-gray-700 mb-1 pt-8">{t('contact.callus')}</h6>
                            <div className="mb-5 mb-md-0 pb-8">
                                <h4 className="text-primary">+30 698 375 3499</h4>
                            </div>
                        </a>
                        <a href="mailto:wesolve@gmail.com" className="col-12 col-md-4 text-center hover-shadow text-decoration-none">
                            <h6 className="text-uppercase text-gray-700 mb-1 pt-8">{t('contact.emailus')}</h6>
                            <div className="mb-5 mb-md-0 pb-8">
                                <h4 className="text-primary">wesolve@gmail.com</h4>
                            </div>
                        </a>
                    </div>
                </div>
            </section>

            <section className="pt-8 pt-md-11 pb-8 pb-md-14">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-10 col-lg-8 text-center">
                            <h2 className="font-weight-bold">{t('contact.form_heading')}</h2>
                            <p className="font-size-lg text-muted mb-7 mb-md-9">{t('contact.form_subheading')}</p>
                        </div>
                    </div>
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-12 col-lg-10">
                            <form name="contact" method="POST" data-netlify="true" data-aos="fade-up">
                                <label htmlFor="input-hidden" type="hidden" />
                                <input id="input-hidden" type="hidden" name="form-name" value="contact" />
                                <div className="row">
                                    <div className="col-12 col-md-6">
                                        <div className="form-group mb-5">
                                            <label htmlFor="contactName" className="text-muted font-weight-bold">
                                                {t('contact.name_label')}
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                className="form-control custom-input-primary form-control shadow shadow-lg"
                                                id="contactName"
                                                placeholder={t('contact.name_placeholder')}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <div className="form-group mb-5">
                                            <label htmlFor="contactEmail" className="text-muted font-weight-bold">
                                                Email
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                className="form-control custom-input-primary form-control shadow shadow-lg"
                                                id="contactEmail"
                                                placeholder={t('contact.email_placeholder')}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="form-group mb-5">
                                            <label htmlFor="contactReason" className="text-muted font-weight-bold">
                                                {t('contact.reason_label')}
                                            </label>
                                            <input
                                                type="text"
                                                name="reason"
                                                className="form-control custom-input-primary form-control shadow shadow-lg"
                                                id="contactReason"
                                                placeholder={t('contact.reason_placeholder')}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-12">
                                        <div className="form-group mb-7 mb-md-9">
                                            <label htmlFor="contactMessage" className="text-muted font-weight-bold">
                                                {t('contact.message_label')}
                                            </label>
                                            <textarea
                                                name="message"
                                                className="form-control custom-input-primary form-control shadow shadow-lg "
                                                id="contactMessage"
                                                rows="5"
                                                placeholder={t('contact.message_placeholder')}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row justify-content-center">
                                    <div className="col-auto">
                                        <button type="submit" className="btn btn-primary shadow shadow-lg lift">
                                            {t('contact.cta')}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default WithIntl()(Contact);
